import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { hdmiPort } from './TopologyOverview';
import { isVideoConnection } from '../../utils/equipment';
import InputAutomationTrainer from './InputAutomationTrainer';
import { cecAllowed } from './sourceActivation';

const tokenHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

export default function GuidedSourceSetup({
  areaId,
  topology,
  devices,
  tvInputReports,
  onTopologyChange,
  onClose,
}) {
  const screens = useMemo(() => (topology?.screens || []).map((entry) => ({ ...entry,
    players: (entry.players || []).filter((player) => isVideoConnection(player,
      (devices || []).find((device) => Number(device.id) === Number(player.deviceId || player.id)))),
  })).filter((entry) => (
    entry.displayKind !== 'unmanaged' && entry.televisionDeviceId && (entry.players || []).length
  )), [topology, devices]);
  const [screenId, setScreenId] = useState(String(screens[0]?.id || ''));
  const screen = screens.find((entry) => String(entry.id) === screenId) || screens[0] || null;
  const [playerId, setPlayerId] = useState(String(screen?.players?.[0]?.deviceId || screen?.players?.[0]?.id || ''));
  const player = (screen?.players || []).find((entry) => String(entry.deviceId || entry.id) === playerId)
    || screen?.players?.[0] || null;
  const port = hdmiPort(player?.expectedInput);
  const [stage, setStage] = useState('choose');
  const [busy, setBusy] = useState('');
  const [testedMethod, setTestedMethod] = useState(null);
  const [message, setMessage] = useState('Choose the television source you want to prove, then test each method in order.');

  const changeScreen = (nextId) => {
    const nextScreen = screens.find((entry) => String(entry.id) === nextId);
    setScreenId(nextId);
    setPlayerId(String(nextScreen?.players?.[0]?.deviceId || nextScreen?.players?.[0]?.id || ''));
    setStage('choose');
    setTestedMethod(null);
    setMessage('Source changed. Begin with the Input button test.');
  };

  const sendTvAction = async (action, label) => {
    if (!screen?.televisionDeviceId || !port) return;
    setBusy(action);
    setTestedMethod(null);
    setMessage(`${label} sent. Watch the television, then tell Suitestream what happened.`);
    try {
      const response = await axios.post(
        `/api/site-control/targets/${screen.televisionDeviceId}/action`,
        { action, repeat: 1, intervalMs: 35, bypassTrainedAutomation: true },
        { headers: tokenHeaders() }
      );
      if (['failed', 'blocked', 'unsupported'].includes(response.data?.status)) throw new Error('Command was not accepted');
      setTestedMethod(action === 'input' ? 'android_tv_remote_input' : 'android_tv_remote_discrete');
    } catch (error) {
      setMessage(`${label} could not be sent: ${error?.response?.data?.message || error.message}. Continue to the next method.`);
    } finally {
      setBusy('');
    }
  };

  const saveMethod = async (method, successMessage) => {
    if (busy || testedMethod !== method) return;
    const methods = screen.activationStrategy?.inputSwitching?.methods || [];
    const nextTopology = {
      ...topology,
      screens: topology.screens.map((entry) => entry.id !== screen.id ? entry : ({
        ...entry,
        ...(method === 'player_home_cec' ? {
          players: entry.players.map((source) => Number(source.deviceId || source.id) !== Number(player.deviceId || player.id) ? source : ({
            ...source,
            connectionConfig: { ...source.connectionConfig, activationOverride: {
              type: 'player_home_cec', status: 'confirmed', verifiedAt: new Date().toISOString(),
              evidence: { type: 'operator_confirmation', expectedInput: player.expectedInput },
            } },
          })),
        } : {}),
        activationStrategy: {
          ...entry.activationStrategy,
          inputSwitching: {
            ...entry.activationStrategy?.inputSwitching,
            ...(method === 'player_home_cec' ? {} : {
              methods: [method, ...methods.filter((entryMethod) => entryMethod !== method)],
              defaultMethod: method,
            }),
          },
        },
      })),
    };
    setBusy('save');
    try {
      const response = await axios.put(`/api/areas/${areaId}/topology`, nextTopology, { headers: tokenHeaders() });
      onTopologyChange?.(response.data);
      setStage('complete');
      setMessage(successMessage);
    } catch (error) {
      setMessage(error?.response?.data?.message || error.message || 'The successful method could not be saved.');
    } finally {
      setBusy('');
    }
  };

  const testCec = async () => {
    if (!cecAllowed(player)) return;
    setBusy('cec');
    setTestedMethod(null);
    setMessage(`Sending Home to ${player?.name || 'the player'}. Allow a few seconds for HDMI-CEC to activate its source.`);
    try {
      const response = await axios.post(`/api/areas/${areaId}/topology/screens/${screen.id}/activate-source`,
        { sourceDeviceId: Number(player.deviceId || player.id), testCec: true }, { headers: tokenHeaders() });
      if (['failed', 'blocked', 'unsupported'].includes(response.data?.status)) throw new Error('Command was not accepted');
      setTestedMethod('player_home_cec');
    } catch (error) {
      setMessage(error?.response?.data?.message || error.message || 'Home could not be sent to this player.');
    } finally {
      setBusy('');
    }
  };

  if (!screens.length) {
    return (
      <section className="guided-source-setup">
        <header><div><span>Guided Source Setup</span><h3>Connect a TV and player first</h3></div><button type="button" onClick={onClose}>Close</button></header>
        <p>A managed Logical Screen with at least one mapped HDMI player is required.</p>
      </section>
    );
  }

  return (
    <section className="guided-source-setup">
      <header>
        <div><span>Screen input setup</span><h3>Guided Source Setup</h3><p>Start with the TV input selector, train its navigation, then optionally prove a source shortcut.</p></div>
        <button type="button" onClick={onClose}>Close</button>
      </header>

      <div className="guided-source-setup__target">
        <label>Television<select disabled={Boolean(busy)} value={screen?.id || ''} onChange={(event) => changeScreen(event.target.value)}>{screens.map((entry) => <option key={entry.id} value={entry.id}>{entry.name}</option>)}</select></label>
        <label>Source to test<select disabled={Boolean(busy)} value={player?.deviceId || player?.id || ''} onChange={(event) => { setPlayerId(event.target.value); setStage('choose'); setTestedMethod(null); }}><option value="" disabled>Select a source</option>{(screen?.players || []).map((entry) => <option key={entry.deviceId || entry.id} value={entry.deviceId || entry.id}>{entry.name || `Device ${entry.deviceId || entry.id}`} · {entry.expectedInput || 'port unknown'}</option>)}</select></label>
      </div>

      <ol className="guided-source-setup__methods">
        <li className={stage === 'input' ? 'is-active' : ''}>
          <div><strong>1. TV Input button</strong><p>Check whether Input switches directly or opens the TV’s selector. Menu navigation should be trained below.</p></div>
          <button type="button" disabled={!port || Boolean(busy)} onClick={() => { setStage('input'); sendTvAction('input', 'Input command'); }}>{busy === 'input' ? 'Sending…' : 'Test Input button'}</button>
          {stage === 'input' && <div className="guided-source-setup__answer"><span>Did one press switch directly to the correct source?</span><button type="button" disabled={Boolean(busy) || testedMethod !== 'android_tv_remote_input'} onClick={() => saveMethod('android_tv_remote_input', 'Input saved as the Screen default. Confirm Watch from other starting inputs too.')}>Yes, save it</button><button type="button" disabled={Boolean(busy)} onClick={() => setStage('training')}>Menu opened / train navigation</button></div>}
        </li>
        <li className={stage === 'training' ? 'is-active' : ''}>
          <div><strong>2. Train input navigation</strong><p>Record and verify the route to {player?.expectedInput || 'this source'} without leaving this setup.</p></div>
          <button type="button" disabled={!port || Boolean(busy)} onClick={() => setStage('training')}>Open input trainer</button>
          {stage === 'training' && <div className="guided-source-setup__trainer"><InputAutomationTrainer key={`${screen.id}-${port}`} areaId={areaId} topology={topology} devices={devices} tvInputReports={tvInputReports} initialScreenId={screen.id} initialPort={port} onTopologyChange={onTopologyChange} /></div>}
        </li>
        <li className={stage === 'cec' ? 'is-active' : ''}>
          <div><strong>3. Optional player Home via HDMI-CEC</strong><p>Send Home to this player. Confirmation saves a shortcut for this connection only, leaving the Screen default unchanged.</p></div>
          {!cecAllowed(player) && <p>CEC is blocked, unavailable, or not confirmed available through this distribution path. Use the TV selector.</p>}
          <button type="button" disabled={!cecAllowed(player) || Boolean(busy)} onClick={() => { setStage('cec'); testCec(); }}>{busy === 'cec' ? 'Sending…' : 'Test player Home/CEC'}</button>
          {stage === 'cec' && <div className="guided-source-setup__answer"><span>Did the TV change to this player?</span><button type="button" disabled={Boolean(busy) || testedMethod !== 'player_home_cec'} onClick={() => saveMethod('player_home_cec', 'Home/CEC shortcut confirmed for this connection. The Screen default is unchanged.')}>Yes, save shortcut</button><button type="button" disabled={Boolean(busy)} onClick={() => setStage('training')}>No, use input trainer</button></div>}
        </li>
        <li className={stage === 'direct' ? 'is-active' : ''}>
          <div><strong>4. Optional direct HDMI command</strong><p>Try a discrete HDMI key if this TV supports it.</p></div>
          <button type="button" disabled={!port || Boolean(busy)} onClick={() => { setStage('direct'); sendTvAction(`input_hdmi_${port}`, `Direct HDMI ${port}`); }}>{busy === `input_hdmi_${port}` ? 'Sending…' : `Test HDMI ${port}`}</button>
          {stage === 'direct' && <div className="guided-source-setup__answer"><span>Did the TV switch to the correct source?</span><button type="button" disabled={Boolean(busy) || testedMethod !== 'android_tv_remote_discrete'} onClick={() => saveMethod('android_tv_remote_discrete', 'Direct HDMI switching saved as the Screen default.')}>Yes, save it</button></div>}
        </li>
      </ol>

      <p className={`guided-source-setup__message ${stage === 'complete' ? 'is-success' : ''}`}>{message}</p>
      {stage === 'complete' && <div className="guided-source-setup__complete"><strong>Source setup complete.</strong><button type="button" onClick={onClose}>Finish</button></div>}
    </section>
  );
}
